import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Printer, User } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useProperties, useBookings, useOwners, type Booking, type Property } from '../../../hooks/useManagement';

const eur = (n: number) => `€${Math.round(n).toLocaleString('es-ES')}`;
const CH_COLORS: Record<string, string> = { direct: '#14b8a6', airbnb: '#f43f5e', booking: '#3b82f6', vrbo: '#8b5cf6' };
const CH_LABEL: Record<string, string> = { direct: 'Directo', airbnb: 'Airbnb', booking: 'Booking', vrbo: 'Vrbo' };

/**
 * Owner reports (agencies): pick an end-owner + month, get a clean printable
 * statement of their units' bookings, nights, occupancy and gross revenue.
 * "Save as PDF" via the browser print dialog (a print stylesheet hides the app
 * chrome — see index.css @media print / .print-area).
 */
export default function ManageOwnerReportsPage() {
  const { t } = useTranslation();
  const { data: properties } = useProperties();
  const { data: bookings } = useBookings('all', null);
  const { data: ownersList } = useOwners();

  const nowMonth = useMemo(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  // selectedOwner: '' = all owners (default), 'none' = unassigned, or an owner id.
  const [owner, setOwner] = useState<string>('');
  const [month, setMonth] = useState<string>(nowMonth);
  const [commission, setCommission] = useState<number>(20);

  const selectedOwner = owner;
  // Properties in scope for the selected filter.
  const ownerProps = useMemo<Property[]>(() => {
    const all = properties ?? [];
    if (selectedOwner === '') return all;                         // all owners
    if (selectedOwner === 'none') return all.filter(p => !p.owner_id);
    const id = Number(selectedOwner);
    return all.filter(p => p.owner_id === id);
  }, [properties, selectedOwner]);
  const selectedOwnerObj = (ownersList ?? []).find(o => String(o.id) === selectedOwner);
  const ownerEmail = selectedOwnerObj?.email || '';
  const unassignedCount = (properties ?? []).filter(p => !p.owner_id).length;

  // Bookings for this owner's properties with check-in inside the selected month.
  const [y, m] = month.split('-').map(Number);
  const periodStart = `${month}-01`;
  const periodEnd = `${y}-${String(m).padStart(2, '0')}-${String(new Date(y!, m!, 0).getDate()).padStart(2, '0')}`;
  const monthLabel = new Date(y!, (m! - 1), 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  const rows = useMemo(() => {
    const propIds = new Set(ownerProps.map(p => p.id));
    const list = (bookings ?? []).filter(b =>
      b.property_id != null && propIds.has(b.property_id) &&
      (b.status === 'confirmed' || b.status === 'completed') &&
      b.check_in >= periodStart && b.check_in <= periodEnd);
    return list.sort((a, b) => a.check_in.localeCompare(b.check_in));
  }, [bookings, ownerProps, periodStart, periodEnd]);

  const daysInMonth = new Date(y!, m!, 0).getDate();

  const stats = useMemo(() => {
    const revenue = rows.reduce((s, b) => s + (b.total || 0), 0);
    const nights = rows.reduce((s, b) => s + (b.nights || 0), 0);
    const cleaning = rows.reduce((s, b) => s + (b.cleaning_fee || 0), 0);
    const guests = rows.reduce((s, b) => s + (b.guests || 0), 0);
    const adr = nights ? revenue / nights : 0;
    const avgStay = rows.length ? nights / rows.length : 0;
    const occupancy = ownerProps.length ? Math.min(1, nights / (ownerProps.length * daysInMonth)) : 0;
    const commissionAmount = revenue * (commission / 100);
    const net = revenue - commissionAmount;

    // Per-property breakdown
    const perProperty = ownerProps.map(p => {
      const bs = rows.filter(b => b.property_id === p.id);
      const rev = bs.reduce((s, b) => s + (b.total || 0), 0);
      const n = bs.reduce((s, b) => s + (b.nights || 0), 0);
      return { property: p, count: bs.length, nights: n, revenue: rev, occupancy: Math.min(1, n / daysInMonth) };
    }).sort((a, b) => b.revenue - a.revenue);

    // Channel mix
    const chMap = new Map<string, { count: number; revenue: number }>();
    for (const b of rows) {
      const c = b.channel || 'direct';
      const e = chMap.get(c) ?? { count: 0, revenue: 0 };
      e.count++; e.revenue += b.total || 0; chMap.set(c, e);
    }
    const channels = Array.from(chMap.entries()).map(([key, v]) => ({ key, ...v })).sort((a, b) => b.revenue - a.revenue);

    return { revenue, nights, cleaning, guests, adr, avgStay, occupancy, commissionAmount, net, count: rows.length, perProperty, channels };
  }, [rows, ownerProps, daysInMonth, commission]);

  const nameOf = (id: number | null) => ownerProps.find(p => p.id === id)?.name || '—';
  const chartData = stats.perProperty.filter(r => r.revenue > 0).map(r => ({ name: (r.property.name || '').slice(0, 18), revenue: Math.round(r.revenue) }));

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="no-print">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText size={22} className="text-teal-600" />
          {t('portal.manage.reports.title', 'Informes de propietarios')}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t('portal.manage.reports.subtitle', 'Genera un extracto mensual por propietario y guárdalo como PDF.')}</p>
      </div>

      {/* Controls */}
      <div className="no-print flex flex-wrap items-end gap-3 bg-white rounded-2xl border border-slate-200 p-4">
        <label className="text-sm">
          <span className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.reports.owner', 'Propietario')}</span>
          <select value={selectedOwner} onChange={e => setOwner(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm min-w-[14rem]">
            <option value="">{t('portal.manage.reports.allOwners', 'Todos los propietarios')} ({(properties ?? []).length})</option>
            {(ownersList ?? []).map(o => <option key={o.id} value={String(o.id)}>{o.name} ({o.property_count})</option>)}
            {unassignedCount > 0 && <option value="none">{t('portal.manage.reports.noOwner', 'Sin propietario')} ({unassignedCount})</option>}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.reports.month', 'Mes')}</span>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <label className="text-sm">
          <span className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.reports.commission', 'Comisión gestión (%)')}</span>
          <input type="number" min={0} max={100} value={commission} onChange={e => setCommission(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm w-24" />
        </label>
        <button onClick={() => window.print()} className="ml-auto inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 text-sm font-semibold">
          <Printer size={16} /> {t('portal.manage.reports.print', 'Imprimir / Guardar PDF')}
        </button>
      </div>

      {/* Printable report */}
      <div className="print-area bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-6">
        <div className="flex items-start justify-between border-b border-slate-200 pb-4">
          <div>
            <div className="text-lg font-extrabold tracking-tight text-slate-900">KANARIX</div>
            <div className="text-sm text-slate-500 mt-0.5">{t('portal.manage.reports.statement', 'Extracto del propietario')} · {monthLabel}</div>
          </div>
          <div className="text-right text-sm">
            <div className="font-semibold text-slate-800 flex items-center gap-1.5 justify-end"><User size={14} className="text-slate-400" /> {selectedOwner === '—' ? t('portal.manage.reports.noOwner', 'Sin propietario') : selectedOwner}</div>
            {ownerEmail && <div className="text-slate-400">{ownerEmail}</div>}
            <div className="text-slate-400">{ownerProps.length} {t('portal.manage.reports.units', 'alojamientos')}</div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Sum label={t('portal.manage.reports.sumBookings', 'Reservas')} value={String(stats.count)} />
          <Sum label={t('portal.manage.reports.sumNights', 'Noches')} value={String(stats.nights)} />
          <Sum label={t('portal.manage.reports.sumOccupancy', 'Ocupación')} value={`${Math.round(stats.occupancy * 100)}%`} />
          <Sum label={t('portal.manage.reports.sumAdr', 'ADR')} value={eur(stats.adr)} />
          <Sum label={t('portal.manage.reports.sumGuests', 'Huéspedes')} value={String(stats.guests)} />
          <Sum label={t('portal.manage.reports.sumStay', 'Estancia media')} value={`${stats.avgStay.toFixed(1)} n`} />
          <Sum label={t('portal.manage.reports.sumCleaning', 'Limpieza')} value={eur(stats.cleaning)} />
          <Sum label={t('portal.manage.reports.sumRevenue', 'Ingresos brutos')} value={eur(stats.revenue)} highlight />
        </div>

        {/* Liquidation: gross → commission → net to owner */}
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 text-sm"><span className="text-slate-600">{t('portal.manage.reports.gross', 'Ingresos brutos')}</span><span className="font-medium text-slate-800">{eur(stats.revenue)}</span></div>
          <div className="flex items-center justify-between px-4 py-2.5 text-sm border-t border-slate-100"><span className="text-slate-600">{t('portal.manage.reports.commissionLine', 'Comisión de gestión')} ({commission}%)</span><span className="font-medium text-rose-600">− {eur(stats.commissionAmount)}</span></div>
          <div className="flex items-center justify-between px-4 py-3 text-sm bg-teal-50 border-t border-teal-100"><span className="font-semibold text-teal-800">{t('portal.manage.reports.net', 'Neto al propietario')}</span><span className="font-bold text-teal-800 text-base">{eur(stats.net)}</span></div>
        </div>

        {/* Revenue by property chart + channel mix */}
        {chartData.length > 0 && (
          <div className="grid md:grid-cols-3 gap-5">
            <div className="md:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.reports.byProperty', 'Ingresos por alojamiento')}</div>
              <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 34)}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip formatter={(v: number) => eur(v)} />
                  <Bar dataKey="revenue" radius={[0, 6, 6, 0]} fill="#14b8a6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.reports.byChannel', 'Por canal')}</div>
              <div className="space-y-2">
                {stats.channels.map(c => (
                  <div key={c.key} className="text-sm">
                    <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: CH_COLORS[c.key] }} /> {CH_LABEL[c.key] || c.key}</span><span className="text-slate-500">{eur(c.revenue)}</span></div>
                    <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${stats.revenue ? (c.revenue / stats.revenue) * 100 : 0}%`, background: CH_COLORS[c.key] }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Per-property breakdown */}
        {stats.perProperty.length > 1 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.reports.perProperty', 'Resumen por alojamiento')}</div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                <th className="py-2 pr-3">{t('portal.manage.reports.colProperty', 'Alojamiento')}</th>
                <th className="py-2 pr-3 text-right">{t('portal.manage.reports.colBk', 'Reservas')}</th>
                <th className="py-2 pr-3 text-right">{t('portal.manage.reports.colNights', 'Noches')}</th>
                <th className="py-2 pr-3 text-right">{t('portal.manage.reports.sumOccupancy', 'Ocupación')}</th>
                <th className="py-2 text-right">{t('portal.manage.reports.colTotal', 'Total')}</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {stats.perProperty.map(r => (
                  <tr key={r.property.id}>
                    <td className="py-2 pr-3 text-slate-700">{r.property.name}</td>
                    <td className="py-2 pr-3 text-right text-slate-600">{r.count}</td>
                    <td className="py-2 pr-3 text-right text-slate-600">{r.nights}</td>
                    <td className="py-2 pr-3 text-right text-slate-600">{Math.round(r.occupancy * 100)}%</td>
                    <td className="py-2 text-right font-medium text-slate-800">{eur(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Booking lines */}
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('portal.manage.reports.detail', 'Detalle de reservas')}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                <th className="py-2 pr-3">{t('portal.manage.reports.colProperty', 'Alojamiento')}</th>
                <th className="py-2 pr-3">{t('portal.manage.reports.colGuest', 'Huésped')}</th>
                <th className="py-2 pr-3">{t('portal.manage.reports.colDates', 'Fechas')}</th>
                <th className="py-2 pr-3 text-right">{t('portal.manage.reports.colNights', 'Noches')}</th>
                <th className="py-2 text-right">{t('portal.manage.reports.colTotal', 'Total')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((b: Booking) => (
                <tr key={b.id}>
                  <td className="py-2 pr-3 text-slate-700">{nameOf(b.property_id)}</td>
                  <td className="py-2 pr-3 text-slate-600">{b.guest_name}</td>
                  <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{b.check_in} → {b.check_out}</td>
                  <td className="py-2 pr-3 text-right text-slate-600">{b.nights}</td>
                  <td className="py-2 text-right font-medium text-slate-800">{eur(b.total || 0)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-400">{t('portal.manage.reports.empty', 'Sin reservas en este periodo.')}</td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-bold text-slate-900">
                  <td className="py-2.5" colSpan={3}>{t('portal.manage.reports.total', 'Total')}</td>
                  <td className="py-2.5 text-right">{stats.nights}</td>
                  <td className="py-2.5 text-right">{eur(stats.revenue)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
          {t('portal.manage.reports.note', 'Ingresos brutos de reservas confirmadas con entrada en el mes seleccionado. Comisiones y tasas se liquidan por separado.')}
        </p>
      </div>
    </div>
  );
}

function Sum({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${highlight ? 'border-teal-200 bg-teal-50' : 'border-slate-200 bg-slate-50'}`}>
      <div className={`text-lg font-bold ${highlight ? 'text-teal-800' : 'text-slate-800'}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
